import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload, faTrash, faDownload, faSpinner, faLanguage
} from '@fortawesome/free-solid-svg-icons';
import MessageBox from './MessageBox';
import ConfirmDialog from './ConfirmDialog';

const MAX_IMAGES = 250;
const BATCH_SIZE = 30;

// Urdu rendering: try Jameel Noori Nastaleeq first (if user has it locally),
// then fall back to widely-distributed Urdu Nastaleeq fonts.
const URDU_FONT_STACK =
  '"Jameel Noori Nastaleeq", "Noto Nastaliq Urdu", "Nafees Nastaleeq", "Aasar Unicode", serif';

function csvEscape(value) {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export default function OCRTab() {
  const API_BASE_URL = window.API_BASE_URL;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });
  const [editing, setEditing] = useState(null); // { id, field, value }
  const fileInputRef = useRef(null);

  const showMessage = (text, type = 'info') => setMessage({ show: true, text, type });
  const showConfirm = (text, callback) => setConfirm({ show: true, text, onConfirm: callback });
  const handleConfirm = () => {
    const cb = confirm.onConfirm;
    setConfirm({ show: false, text: '', onConfirm: null });
    if (cb) setTimeout(() => cb(), 50);
  };
  const handleCancel = () => setConfirm({ show: false, text: '', onConfirm: null });

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('authToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }, []);

  // Initial load of saved rows
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/ocr`, { headers: authHeaders() });
        const data = await res.json();
        if (!cancelled && data.success) setRows(data.rows || []);
      } catch (err) {
        if (!cancelled) showMessage('Failed to load OCR results: ' + err.message, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API_BASE_URL, authHeaders]);

  const handleFilesPicked = async (eventOrFiles) => {
    const fileList = eventOrFiles?.target?.files || eventOrFiles;
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) {
      showMessage('No image files selected', 'error');
      return;
    }
    if (files.length > MAX_IMAGES) {
      showMessage(`Too many images (${files.length}). Maximum is ${MAX_IMAGES} per upload.`, 'error');
      return;
    }
    if (fileInputRef.current) fileInputRef.current.value = '';

    setProcessing(true);
    setProgress({ done: 0, total: files.length });

    try {
      const newRows = [];
      for (let i = 0; i < files.length; i += BATCH_SIZE) {
        const batch = files.slice(i, i + BATCH_SIZE);
        const form = new FormData();
        batch.forEach(f => form.append('files', f, f.name));
        const res = await fetch(`${API_BASE_URL}/ocr/process`, {
          method: 'POST',
          headers: authHeaders(),
          body: form
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.message || `Batch ${Math.floor(i / BATCH_SIZE) + 1} failed`);
        }
        newRows.push(...(data.rows || []));
        setProgress({ done: Math.min(i + batch.length, files.length), total: files.length });
        // Show progress incrementally
        setRows(prev => [...prev, ...(data.rows || [])]);
      }
      showMessage(`OCR complete — ${newRows.length} image(s) processed.`, 'success');
    } catch (err) {
      showMessage('OCR failed: ' + err.message, 'error');
    } finally {
      setProcessing(false);
      setProgress({ done: 0, total: 0 });
    }
  };

  // Drag-and-drop
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (processing) return;
    handleFilesPicked(e.dataTransfer.files);
  };

  // Inline editing
  const beginEdit = (id, field, value) => setEditing({ id, field, value });
  const onEditChange = (val) => setEditing(prev => prev ? { ...prev, value: val } : prev);

  const commitEdit = async () => {
    if (!editing) return;
    const { id, field, value } = editing;
    setEditing(null);
    const original = rows.find(r => r.id === id);
    if (!original || original[field] === value) return;
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
    try {
      const res = await fetch(`${API_BASE_URL}/ocr/${id}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        // revert on failure
        setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: original[field] } : r));
        showMessage('Save failed: ' + (data.message || 'unknown error'), 'error');
      }
    } catch (err) {
      setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: original[field] } : r));
      showMessage('Save failed: ' + err.message, 'error');
    }
  };

  const onEditKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.target.blur();
    } else if (e.key === 'Escape') {
      setEditing(null);
    }
  };

  const deleteRow = (id) => {
    showConfirm('Delete this OCR row?', async () => {
      const prev = rows;
      setRows(prev.filter(r => r.id !== id));
      try {
        const res = await fetch(`${API_BASE_URL}/ocr/${id}`, {
          method: 'DELETE',
          headers: authHeaders()
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          setRows(prev);
          showMessage('Delete failed: ' + (data.message || 'unknown'), 'error');
        }
      } catch (err) {
        setRows(prev);
        showMessage('Delete failed: ' + err.message, 'error');
      }
    });
  };

  const deleteAll = () => {
    if (rows.length === 0) return;
    showConfirm(`Delete all ${rows.length} OCR rows? This cannot be undone.`, async () => {
      const prev = rows;
      setRows([]);
      try {
        const res = await fetch(`${API_BASE_URL}/ocr/all`, {
          method: 'DELETE',
          headers: authHeaders()
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          setRows(prev);
          showMessage('Delete-all failed: ' + (data.message || 'unknown'), 'error');
        }
      } catch (err) {
        setRows(prev);
        showMessage('Delete-all failed: ' + err.message, 'error');
      }
    });
  };

  const downloadCsv = () => {
    if (rows.length === 0) return;
    const header = ['Sr No.', 'Urdu', 'image_name'].map(csvEscape).join(',');
    const lines = rows.map((r, i) => [i + 1, r.urdu_text, r.image_name].map(csvEscape).join(','));
    // Prepend BOM (U+FEFF) so Excel renders UTF-8 Urdu correctly.
    const csv = '﻿' + [header, ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ocr-results-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const progressPct = progress.total > 0
    ? Math.round((progress.done / progress.total) * 100)
    : 0;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
          <FontAwesomeIcon icon={faLanguage} style={{ color: '#7c3aed' }} />
          OCR
        </h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleFilesPicked}
            disabled={processing}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={processing}
            style={{
              padding: '10px 18px',
              background: processing ? '#94a3b8' : 'linear-gradient(135deg, #7c3aed, #6d28d9)',
              color: 'white', border: 'none', borderRadius: 8,
              fontWeight: 700, fontSize: 14,
              cursor: processing ? 'not-allowed' : 'pointer',
              boxShadow: processing ? 'none' : '0 2px 8px rgba(124,58,237,0.35)',
              display: 'flex', alignItems: 'center', gap: 8
            }}
          >
            <FontAwesomeIcon icon={processing ? faSpinner : faUpload} spin={processing} />
            {processing ? `Processing ${progress.done}/${progress.total}` : 'Upload Images'}
          </button>
          <button
            onClick={downloadCsv}
            disabled={rows.length === 0}
            style={{
              padding: '10px 18px',
              background: rows.length === 0 ? '#cbd5e1' : 'linear-gradient(135deg, #10b981, #059669)',
              color: 'white', border: 'none', borderRadius: 8,
              fontWeight: 700, fontSize: 14,
              cursor: rows.length === 0 ? 'not-allowed' : 'pointer',
              boxShadow: rows.length === 0 ? 'none' : '0 2px 8px rgba(16,185,129,0.35)',
              display: 'flex', alignItems: 'center', gap: 8
            }}
          >
            <FontAwesomeIcon icon={faDownload} /> Download CSV
          </button>
          <button
            onClick={deleteAll}
            disabled={rows.length === 0}
            style={{
              padding: '10px 18px',
              background: rows.length === 0 ? '#cbd5e1' : '#dc2626',
              color: 'white', border: 'none', borderRadius: 8,
              fontWeight: 700, fontSize: 14,
              cursor: rows.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8
            }}
          >
            <FontAwesomeIcon icon={faTrash} /> Delete All
          </button>
        </div>
      </div>

      <div
        onDragOver={onDragOver}
        onDrop={onDrop}
        style={{
          border: '2px dashed #cbd5e1',
          borderRadius: 12,
          padding: '18px',
          marginBottom: 16,
          textAlign: 'center',
          color: 'var(--text-secondary, #64748b)',
          fontSize: 13,
          background: 'var(--surface, #f8fafc)'
        }}
      >
        Drop up to {MAX_IMAGES} images here, or click "Upload Images" above.
      </div>

      {processing && progress.total > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
            <span>Batch progress</span>
            <span>{progress.done} / {progress.total} ({progressPct}%)</span>
          </div>
          <div style={{ height: 8, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{
              width: `${progressPct}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #7c3aed, #6d28d9)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      )}

      <div style={{
        background: 'var(--panel-bg, white)',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 70 }} />
            <col />
            <col style={{ width: 240 }} />
            <col style={{ width: 60 }} />
          </colgroup>
          <thead>
            <tr style={{ background: 'var(--surface, #f1f5f9)', borderBottom: '1px solid #e2e8f0' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: 13, fontWeight: 700 }}>Sr No.</th>
              <th style={{ padding: '12px', textAlign: 'right', fontSize: 13, fontWeight: 700, fontFamily: URDU_FONT_STACK }}>اردو (Urdu)</th>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: 13, fontWeight: 700 }}>image_name</th>
              <th style={{ padding: '12px', textAlign: 'center', fontSize: 13, fontWeight: 700 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading...
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                No OCR results yet. Upload images to get started.
              </td></tr>
            ) : rows.map((r, idx) => {
              const isEditingUrdu = editing && editing.id === r.id && editing.field === 'urdu_text';
              const isEditingName = editing && editing.id === r.id && editing.field === 'image_name';
              return (
                <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-secondary)', fontWeight: 600 }}>{idx + 1}</td>
                  <td
                    onDoubleClick={() => beginEdit(r.id, 'urdu_text', r.urdu_text)}
                    title="Double-click to edit"
                    style={{
                      padding: '10px 12px',
                      direction: 'rtl',
                      textAlign: 'right',
                      fontFamily: URDU_FONT_STACK,
                      fontSize: 22,
                      lineHeight: 2,
                      cursor: 'text',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {isEditingUrdu ? (
                      <textarea
                        value={editing.value}
                        onChange={(e) => onEditChange(e.target.value)}
                        onBlur={commitEdit}
                        onKeyDown={onEditKey}
                        autoFocus
                        rows={Math.max(2, String(editing.value || '').split('\n').length)}
                        style={{
                          width: '100%',
                          direction: 'rtl',
                          textAlign: 'right',
                          fontFamily: URDU_FONT_STACK,
                          fontSize: 22,
                          lineHeight: 2,
                          padding: 8,
                          border: '2px solid #7c3aed',
                          borderRadius: 6,
                          resize: 'vertical',
                          outline: 'none',
                          background: 'white'
                        }}
                      />
                    ) : (
                      r.urdu_text || <span style={{ color: '#cbd5e1', fontFamily: 'inherit', fontSize: 13 }}>(empty)</span>
                    )}
                  </td>
                  <td
                    onDoubleClick={() => beginEdit(r.id, 'image_name', r.image_name)}
                    title="Double-click to edit"
                    style={{ padding: '10px 12px', fontSize: 13, cursor: 'text', wordBreak: 'break-all' }}
                  >
                    {isEditingName ? (
                      <input
                        value={editing.value}
                        onChange={(e) => onEditChange(e.target.value)}
                        onBlur={commitEdit}
                        onKeyDown={onEditKey}
                        autoFocus
                        style={{
                          width: '100%',
                          padding: 6,
                          border: '2px solid #7c3aed',
                          borderRadius: 6,
                          fontSize: 13,
                          outline: 'none'
                        }}
                      />
                    ) : (
                      r.image_name
                    )}
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                    <button
                      onClick={() => deleteRow(r.id)}
                      title="Delete row"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#dc2626',
                        cursor: 'pointer',
                        fontSize: 14,
                        padding: 4
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
        Tip: double-click any cell to edit. Press Enter to save, Esc to cancel.
      </p>

      <MessageBox message={message} onClose={() => setMessage({ show: false, text: '', type: 'info' })} />
      <ConfirmDialog confirm={confirm} onConfirm={handleConfirm} onCancel={handleCancel} />
    </div>
  );
}
